/**
 * Slice 52 — room cover photos. One photo per room, picked from the property's Hospitable photos.
 * Cleaners see it on the room screen; rooms without one show the room name only.
 */
import { normalizeRoomName } from "@/lib/roomUtils";
import { collectListingScanPhotos } from "@/lib/propertyListingPhotos";

export interface RoomCoverRow {
  id: string;
  sort_order: number | null;
  cover_photo_url: string | null;
  /** Owner chose "No photo for this room": cleaners see nothing for it. */
  cover_not_needed?: boolean | null;
}

/** A room is settled when it has a cover photo or is marked as needing none. */
export function roomIsSet(row: RoomCoverRow | undefined): boolean {
  return !!row?.cover_photo_url || !!row?.cover_not_needed;
}

/** Walk order: saved order first, rooms never ordered last (in the order they were added). */
export function sortRoomsWalkOrder<T extends { id: string; created_at: string }>(
  rooms: T[],
  cover: Record<string, RoomCoverRow | undefined>,
): T[] {
  return [...rooms].sort((a, b) => {
    const sa = cover[a.id]?.sort_order;
    const sb = cover[b.id]?.sort_order;
    if (sa != null && sb != null && sa !== sb) return sa - sb;
    if (sa != null && sb == null) return -1;
    if (sa == null && sb != null) return 1;
    return a.created_at.localeCompare(b.created_at);
  });
}

export function coverProgressText(withCover: number, total: number, noPhoto = 0): string {
  if (noPhoto > 0) return `${withCover + noPhoto} of ${total} rooms are set`;
  return `${withCover} of ${total} rooms have a cover photo`;
}

/**
 * Suggested covers: the listing photo already matched to a room by the listing scan.
 * Suggestions only fill rooms that have no saved cover and never count until the owner confirms.
 */
export function suggestCovers(
  rooms: { id: string; name: string }[],
  savedCovers: Record<string, string | null | undefined>,
  referencePhotos: { room_name: string; photo_url: string }[],
): Record<string, string> {
  const scan = collectListingScanPhotos(
    referencePhotos,
    rooms.map((r) => r.name),
  );
  const byRoom = new Map(scan.map((p) => [normalizeRoomName(p.roomName), p.url]));
  const out: Record<string, string> = {};
  for (const room of rooms) {
    if (savedCovers[room.id]) continue;
    const url = byRoom.get(normalizeRoomName(room.name));
    if (url) out[room.id] = url;
  }
  return out;
}

/** "Last checked today, 9:14 am" / "Last checked Oct 20, 9:14 am". */
export function lastCheckedText(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return null;
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }).toLowerCase();
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const day = sameDay ? "today" : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return `Last checked ${day}, ${time}`;
}
